import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Divider, Paper, Link } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { useText } from '../context/TextContext';

const Contact = () => {
  const textData = useText();
  const contactEmail = textData.personalInfo?.email || 'contact@aldeasystems.com';
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isEmailValid, setIsEmailValid] = useState(true);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'email') {
      setIsEmailValid(validateEmail(value));
    }
  };

  const handleSendEmail = () => {
    const { name, message } = formData;
    const subject = encodeURIComponent(`New message from ${name}`);
    const body = encodeURIComponent(message);

    window.location.href = `mailto:${contactEmail}?subject=${subject}&body=${body}`;
  };

  const isFormValid = formData.name.trim() && formData.email.trim() && formData.message.trim() && isEmailValid;

  return (
    <Box sx={{ paddingY: { xs: 4, md: 6 }, maxWidth: '800px', margin: '0 auto' }}>
      <Typography 
        variant="h3" 
        gutterBottom 
        sx={{ 
          fontWeight: 600,
          letterSpacing: '-0.02em',
          marginBottom: 2,
          color: '#0078D4'
        }}
      >
        Contact
      </Typography>
      <Typography 
        variant="body1" 
        paragraph 
        sx={{ 
          fontSize: '1.1rem',
          lineHeight: 1.8,
          color: '#4a4a4a',
          marginBottom: 4,
          fontWeight: 300
        }}
      >
        I&apos;m open to B2B collaboration through Aldea Systems—cloud and data engineering for connected products.
        Reach out through the form below or directly via email.
      </Typography>

      <Paper 
        elevation={0} 
        sx={{ 
          padding: 4, 
          backgroundColor: 'white',
          border: '1px solid #EDEBE9',
          borderRadius: '2px',
          boxShadow: '0 1.6px 3.6px rgba(0,0,0,0.13), 0 0.3px 0.9px rgba(0,0,0,0.11)',
          marginBottom: 4
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 3 }}>
          <EmailIcon sx={{ color: '#0078D4' }} />
          <Box>
            <Typography variant="body2" sx={{ color: '#6a6a6a', fontSize: '0.85rem' }}>
              Email
            </Typography>
            <Link 
              href={`mailto:${contactEmail}`}
              sx={{ 
                color: '#0078D4',
                textDecoration: 'none',
                fontSize: '1rem',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {contactEmail}
            </Link>
          </Box>
        </Box>

        <Divider sx={{ marginY: 3 }} />

        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            fontWeight: 400,
            marginBottom: 3,
            color: '#1a1a1a'
          }}
        >
          Send a Message
        </Typography>

        <TextField 
          name="name" 
          label="Name" 
          fullWidth 
          margin="normal" 
          onChange={handleChange}
          value={formData.name}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#0078D4',
              },
            }
          }}
        />
        <TextField 
          name="email" 
          label="Email" 
          fullWidth 
          margin="normal" 
          onChange={handleChange}
          value={formData.email}
          error={!isEmailValid && formData.email.length > 0} 
          helperText={!isEmailValid && formData.email.length > 0 ? "Invalid email format" : ""}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#0078D4',
              },
            }
          }}
        />
        <TextField 
          name="message" 
          label="Message" 
          fullWidth 
          margin="normal" 
          multiline 
          rows={6} 
          onChange={handleChange}
          value={formData.message}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#0078D4',
              },
            }
          }}
        />
        <Button 
          variant="contained" 
          sx={{ 
            marginTop: 3,
            padding: '10px 32px',
            fontSize: '1rem',
            textTransform: 'none',
            backgroundColor: '#0078D4',
            '&:hover': {
              backgroundColor: '#106EBE'
            }
          }} 
          onClick={handleSendEmail} 
          disabled={!isFormValid}
        >
          Send Message
        </Button>
      </Paper>
    </Box>
  );
};

export default Contact;
